import type { NLPSearchResultSource } from '../../ai-answer-dialog/types';

export type MinionOutputItem = {
	minion_id?: string;
	minion_input?: string;
	minion_output?: string;
	minion_extra_info?: string;
	minion_execution_duration?: number;
};

/**
 * MinionOutput can be either a single item or an array of items.
 * When it's an array, the first item contains the main minion info.
 */
export type MinionOutput = MinionOutputItem | MinionOutputItem[];

/**
 * Represents a single orchestration round/loop containing multiple minions.
 */
export type OrchestrationRound = MinionOutput[];

/**
 * High-level minion info for the Orchestration Overview tab.
 * Excludes the detailed `minion_output` field.
 */
export type MinionOverviewInfo = {
	minion_id?: string;
	minion_input?: string;
	minion_extra_info?: string;
};

export type MinionPluginInvocationInfo = {
	searchResults?: string[];
	rankedDocuments?: string[];
	chunkedDocuments?: string[];
};

export type PluginInvocation = {
	plugin_invocation_id?: string;
	plugin_name?: string;
	plugin_invocation_status?: PluginInvocationStatus;
	plugin_description?: string;
	plugin_extra_info?: string;
	plugin_input?: string;
	plugin_output?: string;
	plugin_error_message?: string | null;
};

export type RankedDocumentChunk = {
	chunk_id: number;
	score: number;
	rank: number;
};

export type RawRankedDocumentsInfoMap = {
	[ari: string]: RankedDocumentChunk[];
};

export type PluginExtraInfo = {
	searchResults?: string[];
	rankedDocuments?: string[];
	chunkedDocuments?: string[];
	rawRankedDocumentsInfoMap?: RawRankedDocumentsInfoMap;
};

export enum PluginName {
	TEAMWORK = 'teamwork',
	PAGE_SEARCH_PLUGIN = 'Page-Search-Plugin',
	SEARCH = 'search',
	PEOPLE = 'people',
}

export enum PluginInvocationStatus {
	PLUGIN_FINISHED = 'PLUGIN_FINISHED',
	PLUGIN_ERRORED = 'PLUGIN_ERRORED',
}

export type TeamworkTeam = {
	type: string;
	ari?: string;
	name?: string;
	description?: string;
	creator?: string;
	memberCount?: number;
	members: string[];
	teamState?: string;
};

export type TeamworkDocument = {
	type: string;
	ari?: string;
	title?: string;
	url?: string;
	author?: string;
	updated?: string;
};

export type TeamworkOutput = {
	header?: string;
	sections: Array<{
		sectionHeader?: string;
		teams?: TeamworkTeam[];
		documents?: TeamworkDocument[];
	}>;
};

export type PeopleProfile = {
	name?: string;
	picture?: string;
	email?: string;
	timezone?: string;
	locale?: string;
	nickname?: string;
	jobTitle?: string;
	location?: string;
	department?: string;
	organization?: string;
};

export type PeopleEntry = {
	header?: string;
	accountId?: string;
	profile: PeopleProfile;
};

export type PeopleOutput = {
	people: PeopleEntry[];
};

export type PageSearchPage = {
	title?: string;
	url?: string;
	space?: string;
	date?: string;
	author?: string;
	snippet?: string;
};

export type PageSearchOutput = {
	header?: string;
	pages: PageSearchPage[];
};

export type ParsedPluginOutput =
	| { type: 'teamwork'; data: TeamworkOutput }
	| { type: 'people'; data: PeopleOutput }
	| { type: 'pageSearch'; data: PageSearchOutput };

export enum MinionName {
	TEAMWORK_AGENT = 'TeamworkAgent',
	SEARCH = 'search',
}

export type FunctionResultType = {
	pluginInvocationInfo?: {
		name?: string;
		status?: FunctionResultStatus;
		output?: string;
	};
};

/**
 * Result of parsing minion_extra_info field.
 */
export type ParsedMinionData = {
	extraInfo: MinionPluginInvocationInfo | string | null;
	parsedOutput: ParsedPluginOutput | null;
	functionResults: FunctionResultType[] | null;
	searchExtraInfo: PluginExtraInfo | null;
};

export enum FunctionResultStatus {
	SUCCESS = 'SUCCESS',
	ERROR = 'ERROR',
}

// --- Data Normalization Types ---

export type RankedDocumentWithChunks = {
	url: string;
	chunks: RankedDocumentChunk[];
};

export type CacheInfo = {
	cache_hit?: boolean;
};

export type AnswerGenerationModel = {
	model_usage?: Record<string, unknown>;
};

/**
 * Raw extra attributes from the AI Answer context.
 * This is the shape of data coming from the backend/context.
 */
export type RawDebuggerData = {
	originalQuery?: string;
	transformedQuery?: string;
	traceId?: string;
	answerGenerationModel?: AnswerGenerationModel;
	cacheInfo?: CacheInfo;
	sources?: NLPSearchResultSource[];
	pluginInvocations?: PluginInvocation[];
	minion_outputs?: MinionOutput[];
	rawJson?: unknown;
};

/**
 * Normalized data for the Request Overview panel.
 */
export type RequestOverviewData = {
	originalQuery: string | null;
	transformedQuery: string | null;
	traceId: string | null;
	modelName: string;
	cacheHit: boolean | null;
	sources: NLPSearchResultSource[];
	invokedPluginNames: string[];
};

/**
 * Normalized data for the Ranked Search Results panel.
 */
export type RankedSearchData = {
	searchExtraInfo: PluginExtraInfo | null;
	rankedDocumentsWithChunks: RankedDocumentWithChunks[];
};

/**
 * Normalized data for the Raw Search Results panel.
 */
export type RawSearchData = {
	searchResults: string[];
};

/**
 * Normalized data for the Plugin Invocations panel.
 */
export type PluginInvocationsData = {
	/** All plugin invocations */
	allPluginInvocations: PluginInvocation[];
	/** Plugin invocations excluding the search plugin (for display in Plugin Invocations panel) */
	nonSearchPluginInvocations: PluginInvocation[];
	/** Whether plugin invocations exist */
	hasPluginInvocations: boolean;
};

/**
 * Normalized data for the Orchestration panels.
 */
export type MinionsData = {
	/** All minion outputs */
	allMinionOutputs: MinionOutput[];
	/** Minion outputs excluding the search minion */
	nonSearchMinionOutputs: MinionOutput[];
	/** Whether minion outputs exist */
	hasMinionOutputs: boolean;
	/** Minion outputs organized by orchestration rounds/loops */
	orchestrationRounds: OrchestrationRound[];
	/** Number of orchestration rounds */
	roundCount: number;
};

/**
 * Normalized data for the Raw JSON panel.
 */
export type RawJsonData = {
	rawJson: unknown;
};

/**
 * Complete normalized data for all debugger panels.
 */
export type NormalizedDebuggerData = {
	/** Data for the Request Overview panel */
	requestOverview: RequestOverviewData;
	/** Data for the Ranked Search Results panel */
	rankedSearch: RankedSearchData;
	/** Data for the Raw Search Results panel */
	rawSearch: RawSearchData;
	/** Data for the Plugin Invocations panel */
	pluginInvocations: PluginInvocationsData;
	/** Data for the Minions Invoked panel */
	minions: MinionsData;
	/** Data for the Raw JSON panel */
	rawJson: RawJsonData;
};
